import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import useIsDarkMode from '../hooks/useIsDarkMode';

// Registrar los elementos necesarios para Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

const ConcursoVistaPrevia = ({ estadisticas, cargando, preguntas }) => {
  const isDark = useIsDarkMode();
  // Agrupar preguntas por categoría si están disponibles
  const preguntasPorCategoria = {};
  if (preguntas && preguntas.length > 0) {
    preguntas.forEach(pregunta => {
      const categoria = pregunta.categoria;
      if (!preguntasPorCategoria[categoria]) {
        preguntasPorCategoria[categoria] = [];
      }
      preguntasPorCategoria[categoria].push(pregunta);
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 dark:bg-gray-800 dark:text-white transition-colors duration-300">
      <h3 className="text-lg sm:text-xl font-semibold mb-4 dark:text-white">Vista Previa del Concurso</h3>
      
      {/* Gráfico de porcentaje de preguntas por categoría */}
      <div className="mb-6">
        <h4 className="text-base sm:text-lg font-medium mb-3 dark:text-white">Distribución de Preguntas por Categoría</h4>
        {cargando ? (
          <div className="flex justify-center items-center h-48 sm:h-64 dark:text-white">
            <p>Cargando estadísticas...</p>
          </div>
        ) : estadisticas.length > 0 ? (
          <div className="h-40 sm:h-64">
            <Doughnut 
              data={{
                labels: estadisticas.map(item => item.categoria),
                datasets: [{
                  data: estadisticas.map(item => item.porcentaje),
                  backgroundColor: [
                    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384',
                    '#C9CBCF', '#4BC0C0', '#FF6384', '#36A2EB', '#FFCE56'
                  ].slice(0, estadisticas.length),
                  borderWidth: 1
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      color: isDark ? 'rgba(226, 232, 240, 0.9)' : '#334155', // Legible en ambos temas
                      font: {
                        size: 9,
                      }
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        return `${label}: ${value}%`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        ) : (
          <div className="flex justify-center items-center h-40 sm:h-64 border border-gray-300 rounded dark:border-gray-600 dark:text-white text-[11px] sm:text-sm text-center px-3">
            <p>Selecciona categorías o etiquetas para ver la distribución</p>
          </div>
        )}
      </div>

      {/* Vista previa de preguntas si están disponibles */}
      {preguntas && preguntas.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base sm:text-lg font-medium mb-3 dark:text-white">Vista Previa de Preguntas</h4>
          <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
            {preguntas.slice(0, 10).map((pregunta, index) => (
              <div key={pregunta.id || index} className="border border-gray-200 rounded p-3 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-xs sm:text-sm">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium dark:text-white truncate" title={pregunta.pregunta}>{pregunta.pregunta}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded dark:bg-blue-900 dark:text-blue-100 truncate max-w-[100px] sm:max-w-none">
                        {pregunta.categoria}
                      </span>
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded dark:bg-green-900 dark:text-green-100 truncate max-w-[100px] sm:max-w-none">
                        {pregunta.etiqueta}
                      </span>
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded dark:bg-purple-900 dark:text-purple-100 truncate max-w-[100px] sm:max-w-none">
                        {pregunta.dificultad}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {preguntas.length > 10 && (
              <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-2">
                +{preguntas.length - 10} preguntas más...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Estadísticas detalladas */}
      {estadisticas.length > 0 && (
        <div>
          <h4 className="text-base sm:text-lg font-medium mb-3 dark:text-white">Estadísticas Detalladas</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-700 text-xs sm:text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 dark:text-white">
                <tr>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-300">Categoría</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-300">Cantidad</th>
                  <th className="px-2 py-1 sm:px-4 sm:py-2 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-300">Porcentaje</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-600 dark:divide-gray-700 dark:text-white">
                {estadisticas.map((item, index) => (
                  <tr key={index} className="dark:bg-gray-600 dark:text-white">
                    <td className="px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap dark:text-white">{item.categoria}</td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap dark:text-white">{item.cantidad}</td>
                    <td className="px-2 py-1 sm:px-4 sm:py-2 whitespace-nowrap dark:text-white">{item.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcursoVistaPrevia;
